/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片选择相关的工具函数和配置
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-11-24 17:01:12
 */
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../app/store'
import {
  selectPhotos as selectPhotosV2,
  selectPhotosNum as selectPhotosNumV2,
  selectCheckedPhotos as selectCheckedPhotosV2,
  selectUncheckedPhotos as selectUncheckedPhotosV2,
  selectCheckAll as selectCheckAllV2,
  setCheckAll as setCheckAllV2,
  clearCheck as clearCheckV2
} from '../../../slices/photosByContentSlice'
import {
  selectPhotos as selectPhotosPrecise,
  selectPhotosNum as selectPhotosNumPrecise,
  selectCheckedPhotos as selectCheckedPhotosPrecise,
  selectUncheckedPhotos as selectUncheckedPhotosPrecise,
  selectCheckAll as selectCheckAllPrecise,
  setCheckAll as setCheckAllPrecise,
  clearCheck as clearCheckPrecise
} from '../../../slices/photosByPreciseSlice'

/**
 * 照片选择数据源类型
 */
export type PhotoSelectionSource = 'content' | 'precise'

/**
 * 选择配置接口
 */
export interface SelectionConfig {
  selectPhotos: (state: RootState) => any[]
  selectPhotosNum: (state: RootState) => number
  selectCheckedPhotos: (state: RootState) => Set<number> | number[]
  selectUncheckedPhotos: (state: RootState) => Set<number> | number[]
  selectCheckAll: (state: RootState) => boolean
  setCheckAllAction: () => any
  clearCheckAction: () => any
}

/**
 * 根据数据源获取选择配置
 * @param dataSource 数据源类型
 * @returns 选择配置对象
 */
export function getSelectionConfig(
  dataSource: PhotoSelectionSource
): SelectionConfig {
  if (dataSource === 'precise') {
    return {
      selectPhotos: selectPhotosPrecise,
      selectPhotosNum: selectPhotosNumPrecise,
      selectCheckedPhotos: selectCheckedPhotosPrecise,
      selectUncheckedPhotos: selectUncheckedPhotosPrecise,
      selectCheckAll: selectCheckAllPrecise,
      setCheckAllAction: setCheckAllPrecise,
      clearCheckAction: clearCheckPrecise
    }
  }

  // 默认返回 content 数据源的配置
  return {
    selectPhotos: selectPhotosV2,
    selectPhotosNum: selectPhotosNumV2,
    selectCheckedPhotos: selectCheckedPhotosV2,
    selectUncheckedPhotos: selectUncheckedPhotosV2,
    selectCheckAll: selectCheckAllV2,
    setCheckAllAction: setCheckAllV2,
    clearCheckAction: clearCheckV2
  }
}

/**
 * 将选中的照片数据标准化为 Set
 * @param rawData 原始数据（可能是 Set 或 Array）
 * @returns Set<number>
 */
export function normalizeToSet(
  rawData: Set<number> | number[] | undefined | null
): Set<number> {
  if (!rawData) {
    return new Set<number>()
  }

  if (rawData instanceof Set) {
    return rawData
  }

  if (Array.isArray(rawData)) {
    return new Set(rawData)
  }

  return new Set<number>()
}

/**
 * 猜你想搜 / 推荐结果请求用的 Filter：时间与人员以 suggestedQueryFilter 为准（P2/P3 放宽后的条件），
 * 未单独存储时退回主筛选 queryFilter。
 */
export function buildSuggestedPhotosQueryFilter(
  mainQueryFilter: any,
  suggestedQueryFilter: any | null | undefined,
  suggestedWords: Array<{ word: string; isChecked: boolean }>
) {
  const base = suggestedQueryFilter ?? mainQueryFilter
  if (!base) return null
  return {
    ...base,
    searchKeywords: [],
    suggestedSearchWords: suggestedWords.filter((w) => w.isChecked)
  }
}

/**
 * 获取当前激活的选择状态（处理主列表与猜你想搜的互斥选择）
 */
export function useActiveSelection(dataSource: PhotoSelectionSource) {
  const selectionConfig = getSelectionConfig(dataSource)
  const photos = useSelector(selectionConfig.selectPhotos)
  const photosNum = useSelector(selectionConfig.selectPhotosNum)
  const rawCheckedPhotos = useSelector(selectionConfig.selectCheckedPhotos)
  const rawUncheckedPhotos = useSelector(selectionConfig.selectUncheckedPhotos)
  const checkAll = useSelector(selectionConfig.selectCheckAll)
  const checkedPhotos = normalizeToSet(rawCheckedPhotos)
  const unCheckedPhotos = normalizeToSet(rawUncheckedPhotos)

  const isContent = dataSource === 'content'

  const suggestedPhotos = useSelector((state: RootState) =>
    isContent ? state.photosByContent.suggestedPhotos : []
  )
  const suggestedPhotosNum = useSelector((state: RootState) =>
    isContent ? state.photosByContent.suggestedPhotosNum : 0
  )
  const rawSuggestedChecked = useSelector((state: RootState) =>
    isContent ? state.photosByContent.suggestedCheckedPhotos : new Set<number>()
  )
  const rawSuggestedUnchecked = useSelector((state: RootState) =>
    isContent
      ? state.photosByContent.suggestedUnCheckedPhotos
      : new Set<number>()
  )
  const suggestedCheckAll = useSelector((state: RootState) =>
    isContent ? state.photosByContent.suggestedCheckAll : false
  )
  const suggestedWords = useSelector((state: RootState) =>
    isContent ? state.photosByContent.suggestedWords : []
  )
  const suggestedQueryFilter = useSelector((state: RootState) =>
    isContent ? state.photosByContent.suggestedQueryFilter : null
  )
  const suggestedCheckedPhotos = normalizeToSet(rawSuggestedChecked)
  const suggestedUnCheckedPhotos = normalizeToSet(rawSuggestedUnchecked)

  const hasSuggestedSelected =
    isContent &&
    ((suggestedCheckAll &&
      suggestedPhotosNum > suggestedUnCheckedPhotos.size) ||
      (!suggestedCheckAll && suggestedCheckedPhotos.size > 0))

  const hasMainSelected =
    (checkAll && photosNum > unCheckedPhotos.size) ||
    (!checkAll && checkedPhotos.size > 0)

  return {
    selectionConfig,
    hasSuggestedSelected,
    hasMainSelected,
    activePhotos: hasSuggestedSelected ? suggestedPhotos : photos,
    activePhotosNum: hasSuggestedSelected ? suggestedPhotosNum : photosNum,
    activeCheckAll: hasSuggestedSelected ? suggestedCheckAll : checkAll,
    activeCheckedPhotos: hasSuggestedSelected
      ? suggestedCheckedPhotos
      : checkedPhotos,
    activeUnCheckedPhotos: hasSuggestedSelected
      ? suggestedUnCheckedPhotos
      : unCheckedPhotos,
    // 原数据保留
    mainPhotos: photos,
    mainPhotosNum: photosNum,
    mainCheckAll: checkAll,
    mainCheckedPhotos: checkedPhotos,
    mainUnCheckedPhotos: unCheckedPhotos,
    suggestedPhotos,
    suggestedPhotosNum,
    suggestedCheckAll,
    suggestedCheckedPhotos,
    suggestedUnCheckedPhotos,
    suggestedWords,
    suggestedQueryFilter
  }
}
